/**
 * "O que resolve estas pendências": os baldes da fila de correção, em ABAS
 * por motivo e numa tabela paginada (2026-10-19). Era uma pilha de um
 * cartão por veículo — 161 cartões e 12 mil pixels num upload de imprensa
 * do devstack. A regra de qual aba é de quem mora em
 * `correction-group-tabs.ts`, pura e testada; aqui só se desenha.
 *
 * As ações continuam as da página (que é quem fala com o servidor e
 * recarrega resumo e baldes): reprocessar um balde, reprocessar os da aba,
 * cadastrar, criar no cadastro, usar um existente e — no veículo ambíguo
 * (2026-11-03) — escolher qual dos veículos com o mesmo nome é o certo.
 */

import { FileSpreadsheet, FileUp, Info, LoaderCircle, RefreshCw, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import type { CatalogImport, CorrectionGroup } from "@/shared/api/api-client";
import { AppLink } from "@/shared/components/AppLink";
import { Pager } from "@/shared/components/Pager";
import { ToolbarSelect } from "@/shared/components/ToolbarSelect";
import { formatInteger } from "@/shared/utils/format";
import { canonicalFieldLabel } from "@/shared/vocabulary/canonical-fields";
import { vehicleLabel } from "@/shared/vocabulary/vehicle-kinds";
import {
  TAB_HINTS,
  TAB_LABELS,
  groupsOfTab,
  tabSummaries,
  type CorrectionTab
} from "./correction-group-tabs";
import {
  createTarget,
  groupKey,
  importGroupTitle,
  isImportGroup,
  isVehicleAmbiguous,
  vehicleHint
} from "./import-group";

const PAGE_SIZES = [10, 25, 50] as const;

/** Abas em que a mensagem da linha diz algo que a aba não diz (o site que
 *  caiu, a coluna que faltou). Nas outras ela só repete o nome do veículo
 *  e a frase de ajuda da aba. */
const MESSAGE_TABS: ReadonlySet<CorrectionTab> = new Set(["scrape", "duration", "other"]);

/** O balde cujas linhas não trazem veículo/canal: a coluna veio vazia, ou
 *  a linha falhou antes de ser lida (parse). */
const NO_CHANNEL_LABEL = "Veículo ou canal não identificado na linha";

/** "Reprocessar o balde desta aba" / "Reprocessar os 12 baldes desta aba". */
export function reprocessTabLabel(idle: number): string {
  if (idle === 1) return "Reprocessar o balde desta aba";
  if (idle === 0) return "Reprocessar os baldes desta aba";
  return `Reprocessar os ${formatInteger(idle)} baldes desta aba`;
}

type SettingsTab = "canais" | "veiculos" | "temas" | "mensagens";

type CorrectionGroupsPanelProps = {
  groups: CorrectionGroup[];
  /** Nomes das marcas, pra dizer de qual é um balde de valor da planilha.
   *  Só aparece quando a empresa tem mais de uma. */
  brandNames: Record<number, string>;
  postSource: "press" | "network" | null;
  activeImport: CatalogImport | null;
  /** `groupKey` do balde com clique em voo. */
  reprocessingGroup: string | null;
  /** "Reprocessar os desta aba" em voo. */
  reprocessingMany: boolean;
  showDownload: boolean;
  downloadingPending: boolean;
  onDownloadPending: () => void;
  /** Abre o pop-up "Enviar pendências corrigidas". */
  onSendSheet?: () => void;
  onReprocess: (group: CorrectionGroup) => void;
  onReprocessMany: (groups: CorrectionGroup[]) => void;
  onChoose: (group: CorrectionGroup) => void;
  /** Veículo ambíguo: usa o veículo escolhido neste upload e reprocessa. */
  onChooseVehicle?: (group: CorrectionGroup, vehicleId: number) => void;
  settingsTabHref?: (tab: SettingsTab, params?: Record<string, string>) => string;
};

export function CorrectionGroupsPanel({
  groups,
  brandNames,
  postSource,
  activeImport,
  reprocessingGroup,
  reprocessingMany,
  showDownload,
  downloadingPending,
  onDownloadPending,
  onSendSheet,
  onReprocess,
  onReprocessMany,
  onChoose,
  onChooseVehicle,
  settingsTabHref
}: CorrectionGroupsPanelProps) {
  const summaries = useMemo(() => tabSummaries(groups), [groups]);
  const [chosenTab, setChosenTab] = useState<CorrectionTab | null>(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);

  // A aba escolhida pode sumir (todos os baldes dela resolvidos); aí vale
  // a primeira que sobrou — nunca uma aba vazia na tela.
  const tab: CorrectionTab | null =
    chosenTab !== null && summaries.some(summary => summary.tab === chosenTab)
      ? chosenTab
      : summaries[0]?.tab ?? null;

  const visible = useMemo(
    () => (tab === null ? [] : groupsOfTab(groups, tab, query)),
    [groups, tab, query]
  );
  // A página fica dentro do que existe: reprocessar some com baldes, e a
  // última página pode deixar de existir.
  const lastPage = Math.max(1, Math.ceil(visible.length / pageSize));
  const safePage = Math.min(page, lastPage);
  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);
  const pageGroups = visible.slice((safePage - 1) * pageSize, safePage * pageSize);

  if (tab === null) return null;
  const summary = summaries.find(item => item.tab === tab);
  const idleOfTab = groupsOfTab(groups, tab, "").filter(group => group.dispatched === 0);
  const showBrand = Object.keys(brandNames).length > 1;

  return (
    <section className="card correction-groups">
      <div className="correction-groups-head">
        <h3 className="correction-groups-title">O que resolve estas pendências</h3>
        {/* O ciclo inteiro em um lugar: a planilha sai daqui com os nomes
            que faltam JÁ preenchidos, a pessoa completa tier/peso, importa
            nos Ajustes e volta pra reprocessar. */}
        <div className="correction-groups-actions">
        {showDownload && (
          <button
            type="button"
            className="secondary-button"
            title="Planilha com duas abas: Cadastro (canais/veículos que faltam, pronta pra completar e importar nos Ajustes) e Linhas pendentes (as linhas que se corrigem uma a uma, pra editar e enviar de volta)"
            disabled={downloadingPending}
            onClick={onDownloadPending}
          >
            {downloadingPending ? <LoaderCircle className="spin" size={15}/> : <FileSpreadsheet size={15}/>}
            Baixar pendências
          </button>
        )}
        {showDownload && onSendSheet && (
          <button
            type="button"
            className="secondary-button"
            title="Suba a aba Linhas pendentes corrigida: as linhas mudadas voltam pra fila e são reprocessadas"
            onClick={onSendSheet}
          >
            <FileUp size={15}/>
            Enviar pendências corrigidas
          </button>
        )}
        </div>
      </div>

      {/* Com um motivo só, a barra de abas era uma pílula solta que lia
          como botão (2026-10-19): aí o motivo vira o subtítulo. */}
      {summaries.length === 1 ? (
        <p className="correction-groups-motive">{TAB_LABELS[tab]}</p>
      ) : (
      <div className="mode-toggle correction-tabs" role="tablist" aria-label="Motivo das pendências">
        {summaries.map(item => (
          <button
            key={item.tab}
            type="button"
            role="tab"
            aria-selected={item.tab === tab}
            className={item.tab === tab ? "active" : ""}
            onClick={() => {
              setChosenTab(item.tab);
              setQuery("");
              setPage(1);
            }}
          >
            {TAB_LABELS[item.tab]}
            <span className="correction-tab-count">{formatInteger(item.lines)}</span>
          </button>
        ))}
      </div>
      )}

      <p className="upload-detail-hint">{TAB_HINTS[tab]}</p>

      {activeImport && (
        <p className="correction-groups-notice">
          <Info size={13}/>
          Importação de catálogo em andamento ({activeImport.filename}) — reprocessar agora
          avaliaria as linhas contra um catálogo que ainda está mudando. O botão volta sozinho
          quando ela terminar.
        </p>
      )}

      <div className="correction-groups-toolbar">
        <div className="search-field">
          <Search size={17} aria-hidden="true" />
          <input
            name="correction-groups-search"
            autoComplete="off"
            aria-label={tab === "imported" ? "Buscar valor" : "Buscar veículo ou canal"}
            value={query}
            onChange={event => {
              setQuery(event.target.value);
              setPage(1);
            }}
            placeholder={tab === "imported" ? "Buscar valor…" : "Buscar veículo ou canal…"}
          />
        </div>
        <span className="correction-groups-summary">
          {summary &&
            `${formatInteger(summary.groups)} ${summary.groups === 1 ? "balde" : "baldes"} · ${formatInteger(summary.lines)} ${summary.lines === 1 ? "linha" : "linhas"}`}
        </span>
        <button
          type="button"
          className="secondary-button"
          disabled={
            reprocessingMany || reprocessingGroup !== null || activeImport !== null || idleOfTab.length === 0
          }
          title={
            activeImport
              ? "Espere a importação de catálogo terminar"
              : idleOfTab.length === 0
                ? "Todos os baldes desta aba já estão na fila do worker"
                : "Manda os baldes desta aba que ainda não estão na fila, um atrás do outro"
          }
          onClick={() => onReprocessMany(idleOfTab)}
        >
          {reprocessingMany ? <LoaderCircle className="spin" size={15}/> : <RefreshCw size={15}/>}
          {reprocessTabLabel(idleOfTab.length)}
        </button>
      </div>

      {visible.length === 0 ? (
        <p className="inline-empty">Nenhum balde com esse nome nesta aba.</p>
      ) : (
        <div className="correction-groups-table-wrap">
          <table className="ai-admin-table correction-groups-table">
            <thead>
              <tr>
                <th scope="col">{tab === "imported" ? "Valor" : "Veículo / canal"}</th>
                <th scope="col" className="is-number">Linhas</th>
                <th scope="col"><span className="sr-only">Ações</span></th>
              </tr>
            </thead>
            <tbody>
              {pageGroups.map(group => (
                <GroupRow
                  key={groupKey(group)}
                  group={group}
                  brandName={
                    showBrand && group.import_brand_id !== null
                      ? brandNames[group.import_brand_id] ?? null
                      : null
                  }
                  postSource={postSource}
                  busy={reprocessingGroup === groupKey(group)}
                  locked={activeImport !== null || reprocessingMany}
                  showMessage={MESSAGE_TABS.has(tab)}
                  onReprocess={onReprocess}
                  onChoose={onChoose}
                  onChooseVehicle={onChooseVehicle}
                  settingsTabHref={settingsTabHref}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Até 10 baldes cabem na primeira página e o rodapé seria só ruído
          ("1–3 de 3"); acima disso ele aparece, com o tamanho. */}
      {visible.length > PAGE_SIZES[0] && (
      <Pager
        page={safePage}
        size={pageSize}
        count={{ total: visible.length }}
        onPage={setPage}
        sizes={PAGE_SIZES}
        onSize={size => {
          setPageSize(size);
          setPage(1);
        }}
      />
      )}
    </section>
  );
}

function GroupRow({
  group,
  brandName,
  postSource,
  busy,
  locked,
  showMessage,
  onReprocess,
  onChoose,
  onChooseVehicle,
  settingsTabHref
}: {
  group: CorrectionGroup;
  brandName: string | null;
  postSource: "press" | "network" | null;
  busy: boolean;
  locked: boolean;
  showMessage: boolean;
  onReprocess: (group: CorrectionGroup) => void;
  onChoose: (group: CorrectionGroup) => void;
  onChooseVehicle?: (group: CorrectionGroup, vehicleId: number) => void;
  settingsTabHref?: (tab: SettingsTab, params?: Record<string, string>) => string;
}) {
  const fromSheet = isImportGroup(group);
  const ambiguous = isVehicleAmbiguous(group);
  const candidates = group.vehicle_candidates ?? [];
  const [vehicleId, setVehicleId] = useState<number | null>(null);
  // `busy` é o clique em voo (o POST que publica na fila); `queued` é o
  // balde já na fila do worker, que é o que demora. Sem o segundo, o
  // botão voltava ao normal assim que o POST respondia e a pessoa achava
  // que não clicou.
  const queued = group.dispatched > 0;
  // Vem do servidor (`catalog_kind`), não de procurar "aba Veículos"
  // dentro da mensagem: o texto é do back e qualquer reescrita dele
  // quebrava este link em silêncio.
  const settingsTab =
    group.catalog_kind === "vehicle" ? "veiculos" : group.catalog_kind === "channel" ? "canais" : null;
  const create = fromSheet ? createTarget(group, postSource) : null;
  return (
    <tr>
      <td>
        <span className="correction-group-name">
          <strong>
            {fromSheet
              ? importGroupTitle(group, brandName)
              : group.channel ?? NO_CHANNEL_LABEL}
          </strong>
          {fromSheet && (
            <small>
              Valor da planilha que não existe no cadastro (
              {canonicalFieldLabel(group.import_dimension ?? "").toLowerCase()}).
            </small>
          )}
          {showMessage && group.sample_message && <small>{group.sample_message}</small>}
          {ambiguous && <small>Na planilha: {vehicleHint(group)}</small>}
        </span>
      </td>
      <td className="is-number">{formatInteger(group.pending)}</td>
      <td>
        <span className="correction-group-actions">
          {/* Links, e não botões (2026-10-26): o cadastro abre em nova
              aba sem a pessoa perder a fila de correção. */}
          {settingsTab && settingsTabHref && (
            <AppLink className="text-button" to={settingsTabHref(settingsTab)}>
              Cadastrar
            </AppLink>
          )}
          {fromSheet && create && settingsTabHref && (
            <AppLink
              className="text-button"
              title="Abre o cadastro já com o nome da planilha; depois volte e reprocesse"
              to={settingsTabHref(create.tab, create.params)}
            >
              Criar no cadastro
            </AppLink>
          )}
          {fromSheet && (
            <button type="button" className="text-button" onClick={() => onChoose(group)}>
              Usar um existente
            </button>
          )}
          {ambiguous && onChooseVehicle && (
            <>
              <ToolbarSelect<string>
                ariaLabel={`Veículo das linhas de ${group.channel ?? ""}`}
                align="right"
                value={vehicleId === null ? "" : String(vehicleId)}
                options={[
                  {
                    value: "",
                    label:
                      candidates.length === 0
                        ? "Nenhum veículo ativo com este nome"
                        : "Escolha o veículo…"
                  },
                  ...candidates.map(candidate => ({
                    value: String(candidate.id),
                    label: vehicleLabel(candidate)
                  }))
                ]}
                onChange={value => setVehicleId(value ? Number(value) : null)}
              />
              <button
                type="button"
                className="secondary-button"
                disabled={vehicleId === null || busy || queued || locked}
                title={
                  vehicleId === null
                    ? "Escolha um dos veículos cadastrados com este nome"
                    : "Vale só para este upload; as linhas do grupo são reprocessadas"
                }
                onClick={() => vehicleId !== null && onChooseVehicle(group, vehicleId)}
              >
                {busy ? <LoaderCircle className="spin" size={15}/> : <RefreshCw size={15}/>}
                Usar este veículo
              </button>
            </>
          )}
          {!ambiguous && (
          <button
            type="button"
            className="secondary-button"
            disabled={busy || queued || locked}
            title={
              locked
                ? "Espere a importação de catálogo ou o reprocessamento da aba terminar"
                : queued
                  ? "As linhas já estão na fila do worker; o balde some quando terminarem"
                  : undefined
            }
            onClick={() => onReprocess(group)}
          >
            {busy || queued ? <LoaderCircle className="spin" size={15}/> : <RefreshCw size={15}/>}
            {queued
              ? `Na fila (${formatInteger(group.dispatched)} de ${formatInteger(group.pending)})`
              : "Reprocessar"}
          </button>
          )}
          {ambiguous && queued && (
            <span className="correction-group-queued">
              <LoaderCircle className="spin" size={15}/>
              Na fila ({formatInteger(group.dispatched)} de {formatInteger(group.pending)})
            </span>
          )}
        </span>
      </td>
    </tr>
  );
}
